import { z } from "zod";
import {
  CONTACT_COMMUNICATION_STATUSES,
  CONTACT_INDIVIDUAL_EXPERT_WITNESS_SPECIALIZATIONS,
  CONTACT_INDIVIDUAL_SUBTYPES,
  CONTACT_INDIVIDUAL_TIMEZONES,
  CONTACT_ORGANIZATION_MEDICAL_PROVIDER_SPECIALIZATIONS,
  CONTACT_ORGANIZATION_SUBTYPES,
  CONTACT_TYPES,
  GENDERS,
  LANGUAGES,
  RACES,
} from "../../static/contactStatics";
import { PhoneNumberSchema } from "../CommonSchemaFields";

export const ContactUserSnapshotSchema = z.object({
  barNumber: z.string().nullish(),
  email: z.string().nullish(),
  enabled: z.boolean().nullish(),
  firstName: z.string().nullish(),
  id: z.string().nullish(),
  lastName: z.string().nullish(),
  middleName: z.string().nullish(),
  owner: z.boolean().nullish(),
  phoneNumber: z.string().nullish(),
  status: z.string().nullish(),
  username: z.string().nullish(),
});

export const ContactPortalAccessSchema = z.object({
  status: z.enum([
    "none",
    "pending",
    "active",
    "expired",
    "declined",
    "revoked",
    "disabled",
    "not_eligible",
  ]),
  activeConnection: z.boolean().optional(),
  invitedEmail: z.string().nullable().optional(),
  accountEmail: z.string().nullable().optional(),
});

export const ContactAddressSchema = z.object({
  dateCreated: z.string(),
  dateModified: z.string(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  county: z.string().nullable(),
  description: z.string().nullable(),
  isPrimary: z.boolean().nullable(),
  label: z.string().nullable(),
  state: z.string().nullable(),
  street1: z.string().nullable(),
  street2: z.string().nullable(),
  zipCode: z.string().nullable(),
  id: z.string(),
  contactId: z.string(),
  firmId: z.string(),
});

export const ContactCommunicationSmsSchema = z.object({
  dateCreated: z.string(),
  dateModified: z.string(),
  direction: z.enum(["OUTBOUND", "INBOUND"]).nullable(),
  errorCode: z.string().nullable(),
  errorMessage: z.string().nullable(),
  fromNumber: z.string().nullable(),
  message: z.string().nullable(),
  s3Keys: z.array(z.string()).nullable(),
  status: z.enum(CONTACT_COMMUNICATION_STATUSES).nullable(),
  toNumber: z.string().nullable(),
  twilioSid: z.string().nullable(),
  id: z.string(),
  contactId: z.string(),
  firmId: z.string(),
  senderSnapshot: ContactUserSnapshotSchema.nullable(),
});

export const ContactCommunicationEmailSchema = z.object({
  dateCreated: z.string(),
  dateModified: z.string(),
  bccEmails: z.array(z.string()).nullable(),
  bodyHtml: z.string().nullable(),
  bodyText: z.string().nullable(),
  ccEmails: z.array(z.string()).nullable(),
  direction: z.enum(["OUTBOUND", "INBOUND"]).nullable(),
  fromEmail: z.string().nullable(),
  subject: z.string().nullable(),
  toEmails: z.array(z.string()).nullable(),
  id: z.string(),
  contactId: z.string(),
  firmId: z.string(),
  user: ContactUserSnapshotSchema.nullable(),
});

export const ContactEmailSchema = z.object({
  dateCreated: z.string(),
  dateModified: z.string(),
  address: z.string().nullable(),
  description: z.string().nullable(),
  isPrimary: z.boolean().nullable(),
  label: z.string().nullable(),
  id: z.string(),
  contactId: z.string(),
  firmId: z.string(),
});

export const ContactFaxSchema = z.object({
  dateCreated: z.string(),
  dateModified: z.string(),
  description: z.string().nullable(),
  extension: z.string().nullable(),
  isPrimary: z.boolean().nullable(),
  label: z.string().nullable(),
  number: PhoneNumberSchema,
  id: z.string(),
  contactId: z.string(),
  firmId: z.string(),
});

export const ContactNoteSchema = z.object({
  dateCreated: z.string(),
  dateModified: z.string(),
  draft: z.boolean().nullable(),
  message: z.string().nullable(),
  publishedDate: z.string().nullable(),
  id: z.string(),
  contactId: z.string(),
  firmId: z.string(),
  authorUserId: z.string().nullable(),
  author: ContactUserSnapshotSchema.nullable(),
});

export const ContactNoteCommentSchema = z.object({
  dateCreated: z.string(),
  dateModified: z.string(),
  draft: z.boolean().nullable(),
  message: z.string().nullable(),
  publishedDate: z.string().nullable(),
  id: z.string(),
  contactId: z.string(),
  noteId: z.string(),
  firmId: z.string(),
  authorUserId: z.string().nullable(),
  author: ContactUserSnapshotSchema.nullable(),
});

export const ContactPhoneSchema = z.object({
  dateCreated: z.string(),
  dateModified: z.string(),
  description: z.string().nullable(),
  extension: z.string().nullable(),
  isPrimary: z.boolean().nullable(),
  label: z.string().nullable(),
  number: PhoneNumberSchema,
  id: z.string(),
  contactId: z.string(),
  firmId: z.string(),
});

export const ContactTagSchema = z.object({
  dateCreated: z.string(),
  dateModified: z.string(),
  color: z.string().nullable(),
  description: z.string().nullable(),
  label: z.string().nullable(),
  id: z.string(),
  firmId: z.string(),
});

export const ContactTagConnectionSchema = z.object({
  dateCreated: z.string(),
  dateModified: z.string(),
  id: z.string(),
  contactId: z.string(),
  tagId: z.string(),
  firmId: z.string(),
  color: z.string().nullable(),
  description: z.string().nullable(),
  label: z.string().nullable(),
});

export const ContactWebsiteSchema = z.object({
  dateCreated: z.string(),
  dateModified: z.string(),
  address: z.string(),
  description: z.string().nullable(),
  label: z.string().nullable(),
  id: z.string(),
  contactId: z.string(),
  firmId: z.string(),
});

export const ContactEventLogSchema = z.object({
  dateCreated: z.string(),
  dateModified: z.string(),
  id: z.string(),
  actionType: z.string().nullable(),
  actorUserId: z.string().nullable(),
  actor: ContactUserSnapshotSchema.nullable(),
  actorType: z.string().nullable(),
  domain: z.string().nullable(),
  message: z.string().nullable(),
  primarySubjectType: z.string().nullable(),
  primarySubjectId: z.string().nullable(),
  changes: z.record(
    z.object({
      from: z.unknown().nullable(),
      to: z.unknown().nullable(),
    }),
  ),
  snapshot: z.unknown().nullable(),
  eventMetadata: z.record(z.unknown()),
  correlationId: z.string().nullable(),
  causationEventId: z.string().nullable(),
  schemaVersion: z.number().nullable(),
  firmId: z.string().nullable(),
});

export const BaseContactSchema = z.object({
  dateCreated: z.string().nullable(),
  dateModified: z.string(),
  image: z.string().nullable(),
  individualDateOfBirth: z.string().nullable(),
  individualDeceased: z.boolean().nullable(),
  individualDriverLicenseNumber: z.string().nullable(),
  individualDriverLicenseState: z.string().nullable(),
  individualExpertWitnessSpecialization: z
    .array(z.enum(CONTACT_INDIVIDUAL_EXPERT_WITNESS_SPECIALIZATIONS))
    .nullable(),
  individualFirstName: z.string().nullable(),
  individualGender: z.enum(GENDERS).nullable(),
  individualLastName: z.string().nullable(),
  individualMiddleName: z.string().nullable(),
  individualMinor: z.boolean().nullable(),
  individualNickname: z.string().nullable(),
  individualPrimaryLanguage: z.enum(LANGUAGES).nullable(),
  individualRace: z.array(z.enum(RACES)).nullable(),
  individualSecondaryLanguage: z.array(z.enum(LANGUAGES)).nullable(),
  individualSmsConsent: z.boolean().nullable(),
  individualSocialSecurityNumber: z.string().nullable(),
  individualSubTypes: z.array(z.enum(CONTACT_INDIVIDUAL_SUBTYPES)).nullable(),
  individualTimezone: z.enum(CONTACT_INDIVIDUAL_TIMEZONES).nullable(),
  individualTitle: z.string().nullable(),
  organizationFoundingYear: z.string().nullable(),
  organizationMedicalProviderAcceptsLiens: z.boolean().nullable(),
  organizationMedicalProviderSpecialization: z
    .array(z.enum(CONTACT_ORGANIZATION_MEDICAL_PROVIDER_SPECIALIZATIONS))
    .nullable(),
  organizationName: z.string().nullable(),
  organizationSubTypes: z
    .array(z.enum(CONTACT_ORGANIZATION_SUBTYPES))
    .nullable(),
  organizationTaxId: z.string().nullable(),
  preferredContactMethod: z.string().nullable(),
  title: z.string().nullable(),
  groupDescription: z.string().nullable(),
  groupName: z.string().nullable(),
  id: z.string(),
  firmId: z.string(),
  type: z.enum(CONTACT_TYPES).nullable(),
});

export type ContactConnection = {
  dateCreated: string;
  dateModified: string;
  description: string | null;
  sourceContactId: string;
  targetContactId: string;
  relationshipType: "related" | "reports_to";
  id: string;
  firmId: string;
  contact: z.infer<typeof BaseContactSchema> | null;
  childConnections: ContactConnection[];
  parentConnections: ContactConnection[];
  relatedConnections: ContactConnection[];
};

export const ContactConnectionSchema: z.ZodType<ContactConnection> = z.lazy(
  () =>
    z.object({
      dateCreated: z.string(),
      dateModified: z.string(),
      description: z.string().nullable(),
      sourceContactId: z.string(),
      targetContactId: z.string(),
      relationshipType: z.enum(["related", "reports_to"]),
      id: z.string(),
      firmId: z.string(),
      contact: BaseContactSchema.nullable(),
      childConnections: z.array(ContactConnectionSchema),
      parentConnections: z.array(ContactConnectionSchema),
      relatedConnections: z.array(ContactConnectionSchema),
    }),
);

/** Contact selection values embedded in matter and share-folder forms. */
export const ContactConnectionInFormSchema = BaseContactSchema.pick({
  id: true,
  type: true,
})
  .extend({
    contactName: z.string().optional(),
  })
  .passthrough()
  .optional();

export type ContactChildConnectionType = ContactConnection;
export type ContactRelatedConnectionType = ContactConnection;

export const ContactSchema = BaseContactSchema.extend({
  portalAccess: ContactPortalAccessSchema.nullish(),
  addresses: z.array(ContactAddressSchema),
  communicationEmails: z.array(ContactCommunicationEmailSchema),
  communicationSms: z.array(ContactCommunicationSmsSchema),
  emails: z.array(ContactEmailSchema),
  eventLogs: z.array(ContactEventLogSchema),
  faxes: z.array(ContactFaxSchema),
  notes: z.array(ContactNoteSchema),
  noteComments: z.array(ContactNoteCommentSchema),
  phones: z.array(ContactPhoneSchema),
  tagConnections: z.array(ContactTagConnectionSchema),
  websites: z.array(ContactWebsiteSchema),
  childConnections: z.array(ContactConnectionSchema),
  parentConnections: z.array(ContactConnectionSchema),
  relatedConnections: z.array(ContactConnectionSchema),
});

export type ContactTypeFromZod = z.infer<typeof ContactSchema>;
